import React, { useMemo, useState } from 'react'
import {
    Chip,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Tooltip,
    Badge,
    Theme,
    Typography,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import BuildIcon from '@material-ui/icons/Build'
import LockIcon from '@material-ui/icons/Lock'
import LockOpenIcon from '@material-ui/icons/LockOpen'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CheckIcon from '@material-ui/icons/Check'
import PersonIcon from '@material-ui/icons/Person'
import { ReactComponent as ClipboardTickSBIcon } from '../../../assets/icons/clipboard-tick.svg'

import {
    Area,
    AreaConfig,
    AreaStatusConfig,
    AreaStatusTag,
    AreaTransferTooltipMap,
    IdBoolMap,
    ModelListMap,
    ModelMap,
    Schedule,
    Unit,
    UnitConfig,
    BaseWorkorder,
    isWorkorderSafe,
    CREATE_WORKORDERS,
    ServiceContractMap,
    WorkSpaceUser,
    WorkorderStatus,
} from '../../../models'
import { getStatusComparisonValue } from '../../../models/WorkOrder/services'
import { TimezoneState } from '../../../contexts/timezone/timezone'
import { Container, Selector } from '../../../components'
import { UnitArea, WorkorderComponent, GhostWorkorderComponent } from './'

import { hasPermission } from '../../../models/Users/services'
import { ReactComponent as UnitNotesIcon } from '../../../assets/icons/unitnotes.svg'
import { ScheduleFilterController } from '../../../hooks/useFilter'
import {
    BulkEditAreaStatusTagRequest,
    AreaStatusTagListThunk,
} from '../../../store'
import { SimpleUnitInspection } from '../../../hooks/useUnitInspections'
import { typographyStyle } from '../../../theme/typographyStyle'
import {
    formatManualUnitReady,
    ManualUnitReadyMark,
    ManualUnitReadyStatus,
    UNIT_READY_API_STATUS,
    UNIT_READY_LABEL,
    UnitReadyLabel,
} from '../unitReady'

interface Props {
    unit: Unit
    areaConfigMap: ModelMap<AreaConfig>
    unitWorkorderMap: ModelListMap<BaseWorkorder>
    // areaLeaseMap: ModelListMap<Lease>
    schedule?: Schedule
    unitConfig?: UnitConfig
    serviceAreaMap: IdBoolMap
    selectedWorkorder?: BaseWorkorder
    style: React.CSSProperties
    editMode: boolean
    editWorkorderSelection: ModelMap<BaseWorkorder>
    areaStatusTagMap: ModelMap<AreaStatusTag>
    areaTransferTooltipMap: AreaTransferTooltipMap
    areaStatusConfigList: AreaStatusConfig[]
    selected: boolean
    damageCount: number
    upstreamContractMap: ServiceContractMap
    vacantAreas: Area[]
    filterController: ScheduleFilterController
    workspaceUser: WorkSpaceUser | undefined
    theme: Theme
    timezoneState: TimezoneState
    unitInspectionUnitMap: ModelMap<SimpleUnitInspection>
    manualUnitReady: Map<number, ManualUnitReadyMark>
    setUnitReadyStatus: (
        unitId: number,
        status: ManualUnitReadyStatus | null,
    ) => void
    onSelectWorkorder: (workorder: BaseWorkorder, unit: Unit) => void
    clickAssign: (unit: Unit) => void
    clickDamage: (unit: Unit) => void
    onClickChangeOrder: (workorder: BaseWorkorder) => void
    clickUnitNotes: (unit: Unit) => void
    bulkEditAreaStatusTag: (
        req: BulkEditAreaStatusTagRequest,
    ) => AreaStatusTagListThunk
    onClickUnitInspection: (simpleUnitInspection: SimpleUnitInspection) => void
}

const AREA_CONTAINER_HEIGHT = 50
const DETAIL_WIDTH = 170

const _SchedulerUnit = (props: Props) => {
    const {
        unit,
        unitConfig,
        areaConfigMap,
        unitWorkorderMap,
        schedule,
        serviceAreaMap,
        selectedWorkorder,
        onSelectWorkorder,
        clickAssign,
        style,
        editMode,
        editWorkorderSelection,
        areaStatusTagMap,
        areaTransferTooltipMap = {},
        areaStatusConfigList,
        clickDamage,
        selected,
        damageCount,
        upstreamContractMap,
        clickUnitNotes,
        vacantAreas,
        filterController,
        workspaceUser,
        theme,
        bulkEditAreaStatusTag,
        unitInspectionUnitMap,
        onClickUnitInspection,
        manualUnitReady,
        setUnitReadyStatus,
    } = props

    const unitWorkorders: BaseWorkorder[] | undefined =
        unitWorkorderMap[unit.id]

    // Get the unit inspection for this unit
    const unitInspection = unitInspectionUnitMap[unit.id]

    // Check if any areas in the unit are locked
    const hasLockedAreas = useMemo(() => {
        return unit.areas.some(
            (area) => areaStatusTagMap[area.id]?.locked === true,
        )
    }, [unit.areas, areaStatusTagMap])

    // Check if all areas in the unit are locked
    const allAreasLocked = useMemo(() => {
        return (
            unit.areas.length > 0 &&
            unit.areas.every(
                (area) => areaStatusTagMap[area.id]?.locked === true,
            )
        )
    }, [unit.areas, areaStatusTagMap])

    // Handle locking/unlocking all areas in the unit
    const handleToggleLockAllAreas = () => {
        if (schedule) {
            bulkEditAreaStatusTag({
                body: {
                    unit_id: unit.id,
                    schedule: schedule.id,
                    locked: !hasLockedAreas,
                },
            })
        }
    }

    // Unit ready badge logic
    const manualMark = manualUnitReady.get(unit.id)
    const [
        unitReadyMenuAnchor,
        setUnitReadyMenuAnchor,
    ] = useState<HTMLElement | null>(null)

    const automaticUnitReadyStatus: UnitReadyLabel = useMemo(() => {
        if (!unitWorkorders || unitWorkorders.length === 0) return 'Not Started'

        const minComplete = getStatusComparisonValue(WorkorderStatus.COMPLETE)
        let allComplete = true
        let anyStarted = false

        for (const wo of unitWorkorders) {
            const val = getStatusComparisonValue(wo.status)
            if (val < minComplete) {
                allComplete = false
            }
            if (val > 0) {
                anyStarted = true
            }
        }

        if (allComplete) return 'Ready'
        if (anyStarted) return 'In Progress'
        return 'Not Started'
    }, [unitWorkorders])

    const unitReadyStatus: UnitReadyLabel = manualMark
        ? UNIT_READY_LABEL[manualMark.status]
        : automaticUnitReadyStatus

    const unitReadyColor = (label: UnitReadyLabel) => {
        switch (label) {
            case 'Ready':
                return '#4caf50'
            case 'In Progress':
                return '#ff9800'
            case 'Not Started':
                return theme.palette.grey[500]
        }
    }
    const unitReadyBadgeColor = unitReadyColor(unitReadyStatus)
    const unitReadyChoices: UnitReadyLabel[] = [
        'Ready',
        'In Progress',
        'Not Started',
    ]

    // State for the area status selector
    const [selectedAreaStatus, setSelectedAreaStatus] = useState<number>(-1)

    // Handle changing the area status for all areas in the unit
    const handleAreaStatusChange = (
        event: React.ChangeEvent<{ value: unknown }>,
    ) => {
        const newAreaStatusId = event.target.value as number
        if (schedule && newAreaStatusId !== -1) {
            bulkEditAreaStatusTag({
                body: {
                    unit_id: unit.id,
                    schedule: schedule.id,
                    area_status_config: newAreaStatusId,
                    locked: true,
                },
            })
            setSelectedAreaStatus(newAreaStatusId)
        }
    }

    const [statusToolTipOpen, setStatusToolTipOpen] = useState(false)

    return (
        <Container style={{ ...style }}>
            <Paper
                style={{
                    boxShadow: theme.shadows[2],
                    margin: theme.spacing(1),
                    flex: 1,
                }}
            >
                <Container
                    style={{
                        flex: 1,
                        height: '100%',
                        ...typographyStyle(theme.typography.body2),
                    }}
                >
                    <Container
                        style={{
                            width: DETAIL_WIDTH,
                            cursor: 'pointer',
                            borderRadius: `${theme.shape.borderRadius} 0 0 ${theme.shape.borderRadius}`,
                            backgroundColor: theme.palette.grey[200],
                            boxShadow: `3px 0px 3px -1px ${theme.palette.grey[500]}`,
                        }}
                        direction="column"
                    >
                        {/* Top half - Unit Info */}
                        <Container
                            style={{
                                padding: theme.spacing(1),
                                marginBottom: theme.spacing(1),
                            }}
                        >
                            <Container
                                style={{
                                    flex: 1,
                                    flexDirection: 'column',
                                    overflowX: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                <span
                                    style={{
                                        fontWeight:
                                            theme.typography.fontWeightBold,
                                        overflowX: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {unit.folder.path}
                                    {unit.folder.name}
                                </span>
                                <span
                                    style={{
                                        fontWeight:
                                            theme.typography.fontWeightBold,
                                        overflowX: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {unit.name}
                                </span>
                                <span
                                    style={{
                                        fontWeight:
                                            theme.typography.fontWeightLight,
                                        overflowX: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {unitConfig?.name ?? '?'}
                                </span>
                            </Container>
                        </Container>

                        {/* Middle section - Action Buttons in a horizontal row */}
                        <Container
                            style={{
                                justifyContent: 'space-around',
                                alignItems: 'center',
                                marginBottom: theme.spacing(1),
                            }}
                        >
                            {schedule?.active ? (
                                <Tooltip title="Assign">
                                    <IconButton
                                        onClick={() => clickAssign(unit)}
                                        disabled={
                                            !hasPermission(
                                                workspaceUser,
                                                CREATE_WORKORDERS,
                                            )
                                        }
                                        size="small"
                                        style={{
                                            margin: theme.spacing(0, 0.5),
                                        }}
                                    >
                                        <AddIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                <div
                                    style={{
                                        width: 28,
                                        height: 28,
                                        margin: theme.spacing(0, 0.5),
                                    }}
                                />
                            )}

                            <Tooltip title="Damages">
                                <IconButton
                                    onClick={() => clickDamage(unit)}
                                    disabled={
                                        schedule === undefined ||
                                        schedule.inspection === undefined ||
                                        schedule.inspection === null
                                    }
                                    size="small"
                                    style={{ margin: theme.spacing(0, 0.5) }}
                                >
                                    <Badge
                                        badgeContent={damageCount}
                                        color="secondary"
                                    >
                                        <BuildIcon fontSize="small" />
                                    </Badge>
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Unit Notes">
                                <Badge
                                    badgeContent={unit.unit_notes_msg_count}
                                    color={
                                        (unit.unread_unit_notes ?? 0) > 0
                                            ? 'secondary'
                                            : 'primary'
                                    }
                                >
                                    <IconButton
                                        onClick={() => clickUnitNotes(unit)}
                                        size="small"
                                        style={{
                                            margin: theme.spacing(0, 0.5),
                                        }}
                                    >
                                        <UnitNotesIcon />
                                    </IconButton>
                                </Badge>
                            </Tooltip>

                            {unitInspection && (
                                <Tooltip
                                    title={
                                        unitInspection.complete_status
                                            ? 'Unit Inspection Complete'
                                            : 'Unit Inspection Incomplete'
                                    }
                                >
                                    <IconButton
                                        size="small"
                                        style={{
                                            margin: theme.spacing(0, 0.5),
                                        }}
                                        onClick={() =>
                                            onClickUnitInspection(
                                                unitInspection,
                                            )
                                        }
                                    >
                                        <ClipboardTickSBIcon
                                            height={24}
                                            width={24}
                                            style={{
                                                filter: unitInspection.complete_status
                                                    ? 'brightness(0) saturate(100%) invert(68%) sepia(32%) saturate(607%) hue-rotate(92deg) brightness(96%) contrast(88%)' // ~ #46b083
                                                    : 'brightness(0) saturate(100%) invert(27%) sepia(98%) saturate(7474%) hue-rotate(1deg) brightness(100%) contrast(110%)', // ~ #e73535
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Container>

                        {/* Bottom section - Area Status Controls */}
                        <Container
                            style={{
                                padding: theme.spacing(1),
                            }}
                        >
                            <Container style={{ alignItems: 'center' }}>
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.body2,
                                        ),
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    All areas:
                                </span>

                                <Container
                                    style={{
                                        width: '100%',
                                        padding: theme.spacing(0, 1, 0, 1),
                                        alignItems: 'center',
                                    }}
                                >
                                    <Tooltip
                                        title={
                                            hasLockedAreas
                                                ? 'Unlock all areas'
                                                : 'Lock all areas'
                                        }
                                    >
                                        <IconButton
                                            onClick={handleToggleLockAllAreas}
                                            disabled={!schedule?.active}
                                            style={{
                                                padding: theme.spacing(0.5),
                                            }}
                                            size="small"
                                        >
                                            {hasLockedAreas ? (
                                                <LockIcon
                                                    style={{
                                                        fontSize: '1.2rem',
                                                        paddingBottom: theme.spacing(
                                                            0.5,
                                                        ),
                                                    }}
                                                    color="secondary"
                                                />
                                            ) : (
                                                <LockOpenIcon
                                                    style={{
                                                        fontSize: '1.2rem',
                                                        paddingBottom: theme.spacing(
                                                            0.5,
                                                        ),
                                                    }}
                                                />
                                            )}
                                        </IconButton>
                                    </Tooltip>

                                    <div
                                        style={{
                                            flex: 1,
                                            margin: theme.spacing(0, 0.5),
                                        }}
                                    />

                                    <div
                                        onMouseEnter={() => {
                                            setStatusToolTipOpen(true)
                                        }}
                                    >
                                        <Tooltip
                                            title="Set status for all areas"
                                            onClose={() =>
                                                setStatusToolTipOpen(false)
                                            }
                                            open={statusToolTipOpen}
                                        >
                                            <div>
                                                <Selector
                                                    disabled={
                                                        !schedule?.active ||
                                                        allAreasLocked
                                                    }
                                                    label=""
                                                    variant="standard"
                                                    customStyle={{
                                                        formControl: {
                                                            margin: 0,
                                                            marginBottom: 2,
                                                        },
                                                        select: {
                                                            margin: 0,
                                                            maxHeight: 20,
                                                            border: '0px',
                                                            fontSize: '0.75rem',
                                                        },
                                                        text: {
                                                            ...typographyStyle(
                                                                theme.typography
                                                                    .caption,
                                                            ),
                                                        },
                                                    }}
                                                    data={[
                                                        {
                                                            id: -1,
                                                            apartment: -1,
                                                            name: '',
                                                            should_service: true,
                                                        },
                                                        ...areaStatusConfigList,
                                                    ]}
                                                    currentValue={
                                                        selectedAreaStatus
                                                    }
                                                    onChange={
                                                        handleAreaStatusChange
                                                    }
                                                    getDisplayString={(
                                                        areaStatusConfig: AreaStatusConfig,
                                                    ) => areaStatusConfig.name}
                                                    onClick={() =>
                                                        setStatusToolTipOpen(
                                                            false,
                                                        )
                                                    }
                                                    onOpen={() => {
                                                        setStatusToolTipOpen(
                                                            false,
                                                        )
                                                    }}
                                                />
                                            </div>
                                        </Tooltip>
                                    </div>
                                </Container>
                            </Container>
                        </Container>

                        <Container
                            style={{
                                marginTop: 'auto',
                                width: '100%',
                            }}
                        >
                            <Tooltip
                                title={
                                    manualMark
                                        ? formatManualUnitReady(manualMark)
                                        : 'Click to change status'
                                }
                                disableHoverListener={Boolean(
                                    unitReadyMenuAnchor,
                                )}
                            >
                                <Chip
                                    icon={
                                        manualMark ? (
                                            <PersonIcon
                                                style={{
                                                    color: '#fff',
                                                    fontSize: 14,
                                                }}
                                            />
                                        ) : unitReadyStatus === 'Ready' ? (
                                            <CheckCircleIcon
                                                style={{
                                                    color: '#fff',
                                                    fontSize: 14,
                                                }}
                                            />
                                        ) : undefined
                                    }
                                    label={unitReadyStatus}
                                    size="small"
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        setUnitReadyMenuAnchor(
                                            event.currentTarget,
                                        )
                                    }}
                                    style={{
                                        width: '100%',
                                        borderRadius: `0 0 0 ${theme.shape.borderRadius}px`,
                                        backgroundColor: unitReadyBadgeColor,
                                        color: '#fff',
                                        fontWeight: 600,
                                        fontSize: 11,
                                        height: 24,
                                        cursor: 'pointer',
                                    }}
                                />
                            </Tooltip>
                            <Menu
                                anchorEl={unitReadyMenuAnchor}
                                open={Boolean(unitReadyMenuAnchor)}
                                onClose={() => setUnitReadyMenuAnchor(null)}
                                getContentAnchorEl={null}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                                PaperProps={{
                                    style: {
                                        minWidth: 220,
                                        paddingTop: 4,
                                        paddingBottom: 4,
                                    },
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    style={{
                                        display: 'block',
                                        padding: '4px 16px 2px',
                                        color: theme.palette.grey[600],
                                        fontWeight: 600,
                                        letterSpacing: 0.4,
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {manualMark
                                        ? 'Manual status'
                                        : 'Set status'}
                                </Typography>
                                {manualMark && (
                                    <Typography
                                        variant="caption"
                                        style={{
                                            display: 'block',
                                            padding: '0 16px 8px',
                                            color: theme.palette.grey[700],
                                            maxWidth: 240,
                                            whiteSpace: 'normal',
                                        }}
                                    >
                                        {formatManualUnitReady(manualMark)}
                                    </Typography>
                                )}
                                {unitReadyChoices.map((label) => {
                                    const selectedLabel =
                                        label === unitReadyStatus
                                    return (
                                        <MenuItem
                                            key={label}
                                            disabled={selectedLabel}
                                            onClick={() => {
                                                setUnitReadyStatus(
                                                    unit.id,
                                                    UNIT_READY_API_STATUS[
                                                        label
                                                    ],
                                                )
                                                setUnitReadyMenuAnchor(null)
                                            }}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 10,
                                            }}
                                        >
                                            <span
                                                style={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: 10,
                                                    backgroundColor: unitReadyColor(
                                                        label,
                                                    ),
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <span style={{ flex: 1 }}>
                                                {label}
                                            </span>
                                            {selectedLabel && (
                                                <CheckIcon
                                                    style={{
                                                        fontSize: 16,
                                                        color:
                                                            theme.palette
                                                                .grey[700],
                                                    }}
                                                />
                                            )}
                                        </MenuItem>
                                    )
                                })}
                                {manualMark && (
                                    <Divider style={{ margin: '4px 0' }} />
                                )}
                                {manualMark && (
                                    <MenuItem
                                        onClick={() => {
                                            setUnitReadyStatus(unit.id, null)
                                            setUnitReadyMenuAnchor(null)
                                        }}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        <span>Use automatic</span>
                                        <Typography
                                            variant="caption"
                                            style={{
                                                color: theme.palette.grey[600],
                                            }}
                                        >
                                            Follow work order progress
                                        </Typography>
                                    </MenuItem>
                                )}
                            </Menu>
                        </Container>
                    </Container>

                    <Container
                        style={{
                            flex: 1,
                            flexDirection: 'column',
                            backgroundColor: selected
                                ? theme.palette.primary.light
                                : undefined,
                            overflowX: 'auto',
                            width: '300px',
                            overflowY: 'hidden',
                        }}
                    >
                        {/* Area Container */}
                        <Container
                            style={{
                                minHeight: AREA_CONTAINER_HEIGHT,
                                maxHeight: AREA_CONTAINER_HEIGHT,
                                flex: 1,
                                paddingLeft: 2,
                                paddingRight: 2,
                            }}
                        >
                            {unit.areas.map((area) => {
                                if (
                                    areaConfigMap[area.area_config]
                                        ?.shows_on_schedule
                                ) {
                                    return (
                                        <UnitArea
                                            key={area.id}
                                            area={area}
                                            areaConfig={
                                                areaConfigMap[area.area_config]
                                            }
                                            schedule={schedule}
                                            isAreaInFilter={
                                                filterController.filter
                                                    .dateFilter.enable
                                                    ? filterController.isAreaInFilter
                                                    : undefined
                                            }
                                            areaStatusTag={
                                                areaStatusTagMap[area.id]
                                            }
                                            areaStatusConfigList={
                                                areaStatusConfigList
                                            }
                                            areaTransferTooltip={
                                                areaTransferTooltipMap[area.id]
                                            }
                                            vacantAreas={vacantAreas}
                                            filter={filterController.filter}
                                        />
                                    )
                                }
                                return <></>
                            })}
                        </Container>
                        <Divider />

                        {/* Workorder Container */}
                        <Container
                            style={{
                                flex: 1,
                            }}
                        >
                            {unitWorkorders
                                ?.filter((workorder) =>
                                    filterController.isWorkorderInFilter(
                                        workorder,
                                        unit,
                                    ),
                                )
                                .map((workorder) => {
                                    if (isWorkorderSafe(workorder)) {
                                        return (
                                            <WorkorderComponent
                                                key={workorder.id}
                                                workspaceUser={workspaceUser}
                                                theme={theme}
                                                workorder={workorder}
                                                unit={unit}
                                                onClick={onSelectWorkorder}
                                                serviceAreaMap={serviceAreaMap}
                                                areaStatusTagMap={
                                                    areaStatusTagMap
                                                }
                                                upstreamContractMap={
                                                    upstreamContractMap
                                                }
                                                onClickChangeOrder={
                                                    props.onClickChangeOrder
                                                }
                                                selected={
                                                    selectedWorkorder?.id ===
                                                    workorder.id
                                                }
                                                timezoneState={
                                                    props.timezoneState
                                                }
                                                inEditSelection={
                                                    editMode &&
                                                    editWorkorderSelection[
                                                        workorder.id
                                                    ] !== undefined
                                                }
                                            />
                                        )
                                    }

                                    return (
                                        <GhostWorkorderComponent
                                            key={workorder.id}
                                            workorder={workorder}
                                            areaStatusTagMap={areaStatusTagMap}
                                            unit={unit}
                                            onClick={onSelectWorkorder}
                                            serviceAreaMap={serviceAreaMap}
                                            selected={
                                                selectedWorkorder?.id ===
                                                workorder.id
                                            }
                                            inEditSelection={
                                                editMode &&
                                                editWorkorderSelection[
                                                    workorder.id
                                                ] !== undefined
                                            }
                                        />
                                    )
                                })}
                        </Container>
                    </Container>
                </Container>
            </Paper>
        </Container>
    )
}

export const SchedulerUnit = React.memo(_SchedulerUnit)
